import {
  Box,
  BoxProps,
  Button,
  Flex,
  Heading,
  SimpleGrid,
  Text,
  useBreakpointValue,
  useColorModeValue
} from '@chakra-ui/react';
import React, { useContext, useEffect } from 'react';
import { NextSeo } from 'next-seo';
import { useRouter } from 'next/router';
import Link from 'next/link';
import { UserScopes } from '@simplyanvil/smartparse-types';
import { Navbar } from '../../../components/dashboard/Navbar';
import { Footer } from '../../../components/chakraUIPro/Footer';
import Waves from '../../../components/Waves';
import { AuthStateContext } from '../../../contexts/AuthContext';
import LogoLoading from '../../../components/LogoLoading';
import FeatureGuard from '../../../components/FeatureGuard';
import UpdateOrganization from '../../../components/dashboard/UpdateOrganisation';
import UpdateBillingInformation from '../../../components/dashboard/UpdateBillingInformation';

const PricingWrapper = (props: BoxProps & { highlight?: boolean }) => {
  const { highlight, ...rest } = props;

  const popularStyles: BoxProps = {
    borderWidth: '2px',
    borderColor: 'blue.300'
  };

  const styles = highlight ? popularStyles : null;

  return (
    <Box
      w='full'
      mx='auto'
      bg={useColorModeValue('brand.100', 'brand.800')}
      px='10'
      pt='8'
      pb='8'
      rounded='lg'
      shadow='base'
      position='relative'
      {...styles}
      {...rest}
    />
  );
};

const OrganizationSettings = () => {
  const headingSize = useBreakpointValue({ base: 'xs', lg: 'sm' });
  const { isAuthenticated, isLoading, currentOrganisation } = useContext(AuthStateContext);
  const router = useRouter();
  const wavesColor = useColorModeValue('brand.600', 'brand.800');
  const colorGradient = useColorModeValue('linear(to-b, brand.600, gray.700)', 'linear(to-b, brand.800, brand.700)');
  // load url using next router
  useEffect(() => {
    const location = router;
    // eslint-disable-next-line no-console
    console.log('router', location);
  }, [router.asPath]);

  if (!isAuthenticated || !currentOrganisation) {
    if (!isLoading && !isAuthenticated) {
      router.push('/');
    }
    return <LogoLoading />;
  }

  // console.log('currentOrganisation', currentOrganisation);

  return (
    <Box as='section'>
      <NextSeo title='Settings | Smart Parse' description='' noindex nofollow />

      <Navbar />
      <Box px={8} pt={10}>
        <Heading size={headingSize} fontWeight='medium'>
          {currentOrganisation.name}
        </Heading>

        <SimpleGrid
          minHeight='-moz-max-content'
          alignItems='flex-start'
          mt={{ base: '10', lg: '24' }}
          columns={{ base: 1, lg: 2 }}
          spacing={{ base: '12', lg: '8' }}
        >
          <UpdateOrganization />
          <PricingWrapper height='80'>
            <Flex direction='column' justify='center'>
              <Text fontSize='2xl' fontWeight='bold' pb='6'>
                Plan details
              </Text>
              <Text size='md'>Current plan</Text>
              <Text mt='2' color={useColorModeValue('brand.600', 'brand.400')}>
                {currentOrganisation.tier || 'free'}
              </Text>
              <FeatureGuard usrScopes={[UserScopes.ORG_UPDATE]}>
                {/* @ts-ignore */}
                {currentOrganisation.billing?.renewDate && (
                  <>
                    <Text size='md'>Previous charge date</Text>
                    <Text mt='2' color={useColorModeValue('brand.600', 'brand.400')}>
                      {/* @ts-ignore */}
                      {currentOrganisation.billing?.renewDate}
                    </Text>
                  </>
                )}
              </FeatureGuard>
            </Flex>

            <div
              style={{
                position: 'absolute',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bottom: 0,
                left: 0,
                width: '100%',
                marginBottom: '20px',
                marginTop: '20px'
              }}
            >
              <Link href={`/dashboard/${currentOrganisation.id}/subscribe`}>
                <Button
                  w='min-intrinsic'
                  minH='3.5rem'
                  rounded='lg'
                  fontWeight='bold'
                  colorScheme='brand'
                  variant='outline'
                >
                  Manage subscription
                </Button>
              </Link>
            </div>
          </PricingWrapper>
          <UpdateBillingInformation />
        </SimpleGrid>
      </Box>

      <Box position='relative' pb='250px'>
        {/* eslint-disable-next-line react-hooks/rules-of-hooks */}
        <Waves color={wavesColor} />
      </Box>

      {/* Footer */}
      {/* eslint-disable-next-line react-hooks/rules-of-hooks */}
      <Box bgGradient={colorGradient}>
        <Box maxW='10xl' mx='auto' px={8} textAlign='left'>
          <Footer />
        </Box>
      </Box>
    </Box>
  );
};

export default OrganizationSettings;
